import React from 'react';
import SectionHeader from './SectionHeader';
import ChoiceRadio from './ChoiceRadio';
import CheckboxInput from './CheckboxInput';
import NotesInput from './NotesInput';
import BeneficiaryRows from './BeneficiaryRows';
import { filledRows } from './percent';
import { SECTION_DEFAULTS, useSectionDefaults } from './defaults';

/**
 * Primary and contingent beneficiary designations.
 *
 * When the household has a spouse or partner we first ask whether they are
 * the sole 100% primary beneficiary; answering Yes skips the primary list
 * entirely, exactly as the paper form does.
 *
 * On the spouse's own page a "same as mine" checkbox copies the first
 * person's lists across — shared children with the same percentages is the
 * common case, and retyping it was the single most-repeated part of the form.
 */
export default function StepBeneficiaries({
  number,
  person = 'primary',
  data,
  onChange,
  spousePresent = false,
  homeAddress,
  primaryBeneficiaries,
}) {
  useSectionDefaults(data, onChange, SECTION_DEFAULTS.beneficiaries);

  const d = data || {};
  const isSpouse = person === 'spouse';
  const p = isSpouse ? 'spouse_bene' : 'bene';
  const who = isSpouse ? "Your Spouse/Partner's" : 'Your';
  const update = (field) => (value) => onChange({ ...d, [field]: value });

  const skipPrimary = spousePresent && d.spouse_is_sole_primary === 'yes';

  // Offered only on the spouse's page, and only once the first person has
  // actually listed someone worth copying.
  const canCopyPrimary = isSpouse
    && (filledRows(primaryBeneficiaries?.primary).length > 0
      || filledRows(primaryBeneficiaries?.contingent).length > 0);
  const copiedFromPrimary = d.same_as_primary === true;

  const toggleCopyPrimary = (checked) => {
    if (!checked) {
      onChange({ ...d, same_as_primary: false });
      return;
    }
    // Deep-copy so later edits on either page stay independent.
    onChange({
      ...d,
      same_as_primary: true,
      spouse_is_sole_primary: primaryBeneficiaries?.spouse_is_sole_primary,
      primary: JSON.parse(JSON.stringify(primaryBeneficiaries?.primary || [])),
      contingent: JSON.parse(
        JSON.stringify(primaryBeneficiaries?.contingent || []),
      ),
    });
  };

  const noContingent = filledRows(d.contingent).length === 0;

  return (
    <div>
      <SectionHeader number={number} title={`${who} Beneficiaries`} />

      <p className="text-muted small">
        If we are opening accounts that require beneficiary designations (such
        as an IRA, Roth IRA, or Transfer on Death account), please provide the
        information below. If we link to existing accounts, we will review the
        current beneficiary designations to confirm they align with your
        wishes. Please include both primary and contingent beneficiaries and
        complete the information as thoroughly as possible.
      </p>

      <div className="alert alert-light border-start border-4 border-danger py-2 small">
        <i className="bi bi-percent me-2 text-danger" />
        Percentages within each group must total exactly <strong>100.00%</strong>.
        Two decimal places are allowed — for equal thirds use 33.33, 33.33 and
        33.34.
      </div>

      <hr className="my-4" />

      {canCopyPrimary && (
        <div className="cashflow-section-card mb-3">
          <CheckboxInput
            id={`${p}-same-as-primary`}
            label="Same beneficiaries and percentages as the first person listed on this form"
            checked={copiedFromPrimary}
            onChange={toggleCopyPrimary}
          />
        </div>
      )}

      {!copiedFromPrimary && spousePresent && (
        <ChoiceRadio
          id={`${p}-sole-primary`}
          inline
          label={
            <>
              Is your spouse or partner your 100% primary beneficiary?{' '}
              <span className="text-danger">*</span>
            </>
          }
          options={[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No — let me list them' },
          ]}
          value={d.spouse_is_sole_primary}
          onChange={update('spouse_is_sole_primary')}
        />
      )}

      {!copiedFromPrimary && (
        <>
          {!skipPrimary && (
            <BeneficiaryRows
              idPrefix={`${p}-primary`}
              title="Primary"
              description="Who should receive these assets first."
              entries={d.primary}
              onChange={update('primary')}
              sameAsAddress={homeAddress}
            />
          )}

          <BeneficiaryRows
            idPrefix={`${p}-contingent`}
            title="Contingent"
            description="Who should receive these assets if none of your primary beneficiaries survive you. Optional, but strongly recommended."
            entries={d.contingent}
            onChange={update('contingent')}
            sameAsAddress={homeAddress}
          />

          {noContingent && (
            // Not a hard requirement — but leaving it silent is how a form
            // comes back with no contingent at all. The acknowledgement is a
            // validation error until ticked, so it also shows up in the
            // Review & Submit "needs your attention" list.
            <div className="alert alert-warning">
              <p className="fw-semibold mb-2">
                <i className="bi bi-exclamation-triangle me-2" />
                Before you proceed… Are you sure you don&rsquo;t want to list
                any contingent beneficiaries?
              </p>
              <p className="small mb-2">
                Contingent beneficiaries inherit only if none of your primary
                beneficiaries survive you. Most clients name at least one.
              </p>
              <CheckboxInput
                id={`${p}-no-contingent-ack`}
                label="Yes — I don't want to list any contingent beneficiaries."
                checked={d.no_contingent_ack === true}
                onChange={update('no_contingent_ack')}
              />
            </div>
          )}
        </>
      )}

      <NotesInput
        value={d.notes}
        onChange={update('notes')}
        label="Anything else we should know about your beneficiaries?"
        placeholder="Optional"
      />
    </div>
  );
}
